// src/components/GameShell.ts — Shell + navegación + mundo Fase 2
import { router, type Route } from '../app/router.js';
import { store } from '../app/state.js';
import { GAME_CONFIG } from '../data/gameConfig.js';
import { LoadingScreen } from './LoadingScreen.js';
import { TitleScreen } from './TitleScreen.js';
import { NewGameScreen } from './NewGameScreen.js';
import { ContinueScreen } from './ContinueScreen.js';
import { OptionsScreen } from './OptionsScreen.js';
import { createWorldView } from './WorldView.js';

export function createGameShell(root: HTMLElement): void {
  root.className = 'game-shell';

  const topbar = document.createElement('header');
  topbar.className = 'topbar';
  topbar.innerHTML = `
    <div class="topbar__brand"><span class="topbar__dot" aria-hidden="true"></span> AETHERIA</div>
    <div class="topbar__meta"><span data-role="save-status">—</span> · <strong data-role="player-name">—</strong></div>
  `;

  const scene = document.createElement('main');
  scene.className = 'scene';
  scene.setAttribute('aria-label', 'Escena del juego');

  const bg = document.createElement('div');
  bg.className = 'scene__bg';
  bg.setAttribute('aria-hidden', 'true');
  const landscape = document.createElement('div');
  landscape.className = 'aetheria-landscape';
  landscape.innerHTML = `
    <svg viewBox="0 0 1200 420" preserveAspectRatio="xMidYMax slice" aria-hidden="true" style="display:block;width:100%;height:100%">
      <defs>
        <linearGradient id="aurora" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stop-color="#7cf0ff" stop-opacity="0.0"/>
          <stop offset="25%" stop-color="#7cf0ff" stop-opacity="0.35"/>
          <stop offset="50%" stop-color="#6a5cff" stop-opacity="0.45"/>
          <stop offset="75%" stop-color="#ff6b9d" stop-opacity="0.22"/>
          <stop offset="100%" stop-color="#ffcf6b" stop-opacity="0.0"/>
        </linearGradient>
        <linearGradient id="island" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="#1e2a5a"/>
          <stop offset="100%" stop-color="#0a1430"/>
        </linearGradient>
      </defs>
      <path d="M0 120 Q300 40 600 110 T1200 90 L1200 0 L0 0 Z" fill="url(#aurora)" opacity="0.95"/>
      <path d="M0 160 Q400 90 700 150 T1200 130 L1200 40 L0 40 Z" fill="url(#aurora)" opacity="0.55"/>
      <g opacity="0.98">
        <path d="M80 300 L140 265 L210 278 L260 295 L240 335 L120 332 Z" fill="url(#island)" stroke="rgba(124,240,255,.22)" stroke-width="1.2"/>
        <path d="M150 332 L148 390 L162 390 L164 332 Z" fill="rgba(124,240,255,.18)" />
        <circle cx="155" cy="394" r="10" fill="rgba(124,240,255,.14)"/>
        <path d="M340 285 L430 240 L560 252 L620 285 L580 340 L380 338 Z" fill="url(#island)" stroke="rgba(255,207,107,.18)" stroke-width="1.2"/>
        <path d="M400 338 L398 392 L418 392 L420 338 Z" fill="rgba(106,92,255,.18)"/>
        <path d="M760 295 L830 260 L920 272 L980 298 L920 334 L780 330 Z" fill="url(#island)" stroke="rgba(106,92,255,.2)" stroke-width="1.2"/>
        <path d="M830 330 L828 390 L848 390 L850 330 Z" fill="rgba(255,207,107,.12)"/>
        <circle cx="150" cy="285" r="6" fill="rgba(61,214,140,.5)"/>
        <circle cx="470" cy="268" r="9" fill="rgba(61,214,140,.45)"/>
        <circle cx="845" cy="282" r="7" fill="rgba(61,214,140,.42)"/>
      </g>
      <g fill="white" opacity="0.9">
        <circle cx="180" cy="48" r="1.3"/><circle cx="520" cy="32" r="1.1"/><circle cx="880" cy="55" r="1.4"/><circle cx="1050" cy="28" r="1"/>
        <circle cx="340" cy="72" r="0.9"/><circle cx="740" cy="68" r="0.9"/>
      </g>
    </svg>
  `;
  bg.appendChild(landscape);

  const footer = document.createElement('footer');
  footer.className = 'footer';
  footer.innerHTML = `
    <span>© ${new Date().getFullYear()} Aetheria · <span style="font-family:var(--font-mono)">${GAME_CONFIG.version}</span> · Exploración Fase 2</span>
    <span><a href="#/title">Título</a> · <a href="#/options">Opciones</a> · <span data-role="api-status" class="chip" style="padding:.18rem .45rem;font-size:.62rem">API …</span></span>
  `;

  root.append(topbar, scene, footer);
  scene.appendChild(bg);

  const apiStatus = footer.querySelector<HTMLSpanElement>('[data-role="api-status"]')!;
  const saveStatus = topbar.querySelector<HTMLSpanElement>('[data-role="save-status"]')!;
  const playerNameEl = topbar.querySelector<HTMLSpanElement>('[data-role="player-name"]')!;

  function refreshTopbar(): void {
    const s = store.get();
    playerNameEl.textContent = s.profile.name;
    const has = (()=>{ try{ return localStorage.getItem(GAME_CONFIG.storageKey)!==null || localStorage.getItem('aetheria:save:v1')!==null; }catch{return false;}})();
    saveStatus.textContent = has ? '● Guardado' : '○ Sin guardar';
    saveStatus.style.color = has ? 'var(--success)' : 'var(--muted)';
  }
  store.subscribe(()=> refreshTopbar());
  refreshTopbar();

  fetch('/api/health').then(async r=>{
    if(r.ok){ const j= await r.json().catch(()=>null) as {ok?:boolean}|null; apiStatus.textContent=j?.ok?'API ✓':'API ✓ (local)'; apiStatus.className='chip chip--ok'; }
    else { apiStatus.textContent='API local'; apiStatus.className='chip'; }
  }).catch(()=>{ apiStatus.textContent='API local'; apiStatus.className='chip'; });

  let currentRoute: Route = 'loading';
  let contentWrap: HTMLElement | null = null;
  let worldViewEl: HTMLElement | null = null;

  function mount(route: Route): void {
    // cleanup mundo previo
    if (worldViewEl && (worldViewEl as any).destroy) {
      try { (worldViewEl as any).destroy(); } catch {}
      worldViewEl = null;
    }
    // si salimos de world hacia title/newgame etc, mostrar bg
    const isWorld = route === 'world';
    bg.style.display = isWorld ? 'none' : '';
    topbar.style.display = isWorld ? 'none' : '';
    footer.style.display = isWorld ? 'none' : '';
    if (isWorld) {
      scene.style.padding = '0';
      scene.style.display = 'block';
    } else {
      scene.style.padding = '';
      scene.style.display = '';
    }

    currentRoute = route;
    if (contentWrap) contentWrap.remove();
    contentWrap = document.createElement('div');
    contentWrap.style.cssText = isWorld
      ? 'position:relative;z-index:1;width:100%;height:100%;display:block;'
      : 'position:relative;z-index:1;width:100%;display:grid;place-items:center;padding:1rem';
    contentWrap.setAttribute('data-route', route);

    let panel: HTMLElement;
    switch(route){
      case 'loading':
        panel = LoadingScreen({ onDone: ()=> router.finishLoading() });
        break;
      case 'title':
        panel = TitleScreen({
          onNewGame: ()=> router.navigate('newgame'),
          onContinue: ()=> router.navigate('world'),
          onOptions: ()=> router.navigate('options')
        });
        break;
      case 'newgame':
        panel = NewGameScreen({
          onStart: ()=>{
            toast('¡Portal abierto! Apareces en Villa Aurelia.');
            router.navigate('world');
          },
          onBack: ()=> router.navigate('title')
        });
        break;
      case 'continue':
        panel = ContinueScreen({
          onContinue: ()=> router.navigate('world'),
          onBack: ()=> router.navigate('title')
        });
        break;
      case 'options':
        panel = OptionsScreen({ onBack: ()=> router.navigate('title') });
        break;
      case 'world': {
        const wv = createWorldView();
        worldViewEl = wv;
        // world ocupa todo
        wv.style.width='100%'; wv.style.height='100vh'; wv.style.height='100dvh';
        panel = wv;
        // foco
        setTimeout(()=> (wv.querySelector('canvas') as HTMLCanvasElement)?.focus(), 120);
        break;
      }
      default:
        panel = TitleScreen({ onNewGame: ()=> router.navigate('newgame'), onContinue:()=> router.navigate('world'), onOptions:()=> router.navigate('options') });
    }

    contentWrap.appendChild(panel);
    scene.appendChild(contentWrap);
    if(route!=='world' && route!=='loading'){
      panel.setAttribute('tabindex','-1');
      setTimeout(()=> panel.focus({preventScroll:true}), 40);
    }
  }

  router.subscribe(r=> mount(r));
  mount('loading');
  router.init();

  window.addEventListener('keydown', e=>{
    if(e.key==='Escape' && currentRoute!== 'title' && currentRoute!=='loading' && currentRoute!=='world'){
      router.navigate('title');
    }
  });

  // atajo directo W → mundo si hay save (para demo rápida)
  window.addEventListener('keydown', e=>{
    if(e.key.toLowerCase()==='w' && currentRoute==='title' && (e.ctrlKey||e.metaKey)) return;
  });
}

function toast(msg:string): void {
  let stack=document.querySelector<HTMLDivElement>('.toast-stack');
  if(!stack){ stack=document.createElement('div'); stack.className='toast-stack'; document.body.appendChild(stack); }
  const t=document.createElement('div'); t.className='toast'; t.textContent=msg; stack.appendChild(t); setTimeout(()=>t.remove(), 2800);
}
